import { makeStyles } from '@material-ui/core';

const TABLE_HEADER_BACKGROUND = '#EEF3FF';
const EXPANDED_ROW_BACKGROUND = 'rgba(238, 243, 255, 0.2)';
const EXPANDED_ROW_BORDER = '#E0E7FF';

const useStyles = makeStyles(theme => ({
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  idleNote: {
    marginBottom: theme.spacing(2),
  },
  pageHelp: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(0.5),
  },
  toolbarSection: {
    marginBottom: theme.spacing(2),
  },
  reviewPanel: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  reviewPanelHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
  },
  reviewPanelTitle: {
    color: theme.palette.primary.dark,
    fontSize: '0.8125rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
  },
  reviewEyebrow: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    marginBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
  },
  reviewMeta: {
    color: theme.palette.text.secondary,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  reviewActions: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  instructionsPanel: {
    marginTop: theme.spacing(2),
  },
  instructionsSummary: {
    cursor: 'pointer',
    fontWeight: 500,
  },
  instructionsBody: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
  },
  metricsRow: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    marginBottom: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  metricsRowAuto: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
    width: '100%',
  },
  metricsRowTwo: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    marginBottom: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      gridTemplateColumns: '1fr',
    },
  },
  reviewSummaryMetricsRow: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      gridTemplateColumns: '1fr',
    },
  },
  ownerWorkspaceMetricsGrid: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    marginBottom: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  safeguardsGrid: {
    display: 'grid',
    columnGap: theme.spacing(3),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    rowGap: theme.spacing(2.5),
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  safeguardItem: {
    alignItems: 'flex-start',
    margin: 0,
    minWidth: 0,
    width: '100%',
    '& .MuiCheckbox-root': {
      marginTop: -2,
      padding: theme.spacing(0.5),
    },
    '& .MuiFormControlLabel-label': {
      minWidth: 0,
      paddingTop: theme.spacing(0.5),
    },
  },
  safeguardLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  safeguardTitle: {
    fontWeight: 500,
    lineHeight: 1.3,
  },
  safeguardHelp: {
    display: 'block',
    lineHeight: 1.4,
  },
  metricTile: {
    minWidth: 0,
  },
  metricCard: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    height: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  metricLabel: {
    fontWeight: 500,
  },
  metricValueLine: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  metricValue: {
    fontWeight: 600,
    lineHeight: 1.1,
  },
  metricValueMultiline: {
    fontSize: '1.1rem',
    fontWeight: 600,
    lineHeight: 1.3,
    marginTop: theme.spacing(1),
    wordBreak: 'break-word',
  },
  metricBadge: {
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    padding: theme.spacing(0.5, 1),
  },
  metricBadgeInfo: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  metricBadgeWarn: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  metricBadgeSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  metricBadgeDanger: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  searchRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  ownerWorkspaceSearchRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minWidth: 0,
  },
  ownerWorkspaceSearchInputCell: {
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
  },
  filtersRow: {
    alignItems: 'flex-end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  ownerWorkspaceFiltersRow: {
    alignItems: 'flex-end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  searchInputCell: {
    flex: 1,
    minWidth: 220,
  },
  searchInputLabelEllipsis: {
    '& .MuiInputLabel-root': {
      maxWidth: 'calc(100% - 28px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  searchInput: {
    flex: '1 1 240px',
    minWidth: 0,
  },
  filterSelect: {
    minWidth: 180,
  },
  filterSelectCell: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(0.5),
  },
  moreFiltersPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
  },
  assetList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  assetPanel: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  assetSummary: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    listStyle: 'none',
    padding: theme.spacing(2),
    '&::-webkit-details-marker': {
      display: 'none',
    },
  },
  assetSummaryMain: {
    flex: '1 1 320px',
    minWidth: 0,
  },
  assetName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  assetMeta: {
    color: theme.palette.text.secondary,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    fontSize: '0.875rem',
  },
  assetSummaryStatus: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  assetSummaryActions: {
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      width: '100%',
    },
  },
  statusBadge: {
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: theme.spacing(0.25, 1),
  },
  statusBadgeInfo: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  statusBadgeWarn: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  statusBadgeSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusBadgeDanger: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  warningChip: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: 999,
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1),
  },
  assetPanelContent: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
  assetPanelContentFlush: {
    borderTop: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
    overflow: 'hidden',
    padding: 0,
    width: '100%',
  },
  assetSummaryOpen: {
    backgroundColor: TABLE_HEADER_BACKGROUND,
    boxShadow: `inset 3px 0 0 ${theme.palette.secondary.main}`,
  },
  reviewAccordionAssetContent: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    minWidth: 0,
    width: '100%',
  },
  processingActivityReviewList: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxWidth: '100%',
    minWidth: 0,
    padding: theme.spacing(2),
    width: '100%',
  },
  processingActivityReviewEmptyState: {
    padding: theme.spacing(3, 2),
  },
  processingActivityReviewBox: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    cursor: 'pointer',
    padding: theme.spacing(2),
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  processingActivityReviewBoxHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
  },
  processingActivityReviewBoxName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
  },
  processingActivityReviewBoxMenu: {
    flexShrink: 0,
  },
  reviewAccordionActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  reviewAssetSummaryMenu: {
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
  },
  assetToolbar: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  assetToolbarNote: {
    color: theme.palette.text.secondary,
    flex: '1 1 280px',
  },
  tableScrollWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    '& > div': {
      minWidth: 'max-content',
      overflow: 'visible',
    },
    '& > div > table': {
      tableLayout: 'auto',
      width: '100%',
    },
    '& > div > table > thead th': {
      whiteSpace: 'nowrap',
    },
  },
  assetReviewsTableRoot: {
    width: '100%',
  },
  assetReviewsTableSurface: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 10px 28px rgba(16, 24, 40, 0.06)',
    overflow: 'hidden',
    width: '100%',
  },
  assetReviewsSyncedScroll: {
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    '& > div': {
      display: 'inline-block',
      minWidth: '100%',
      verticalAlign: 'top',
    },
    '& > div > table': {
      tableLayout: 'fixed',
    },
    '& > div > table > thead th': {
      whiteSpace: 'nowrap',
    },
  },
  assetReviewsRowScroll: {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  assetReviewsScrollFooter: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  assetReviewsScrollFooterTrack: {
    height: 1,
  },
  assetReviewsSyncedScrollWithActions: {
    '& > div > table > tbody > tr > td': {
      position: 'relative',
      zIndex: 1,
    },
    '& > div > table > thead > tr > th:nth-last-child(1), & > div > table > tbody > tr > td:nth-last-child(1)': {
      boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.06)',
      minWidth: 56,
      padding: theme.spacing(1, 0.5),
      position: 'sticky',
      right: 0,
      verticalAlign: 'middle',
      zIndex: 2,
    },
    '& > div > table > thead > tr > th:nth-last-child(1)': {
      background: TABLE_HEADER_BACKGROUND,
      borderTopRightRadius: 8,
      boxShadow: '-4px 0 8px rgba(16, 24, 40, 0.06)',
      zIndex: 3,
    },
    '& > div > table > tbody > tr > td:nth-last-child(1)': {
      background: theme.palette.common.white,
      zIndex: 2,
      '&::before': {
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent)',
        bottom: 0,
        content: '""',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        width: 8,
        zIndex: 0,
      },
      '& > span': {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
      },
    },
  },
  assetReviewsTableWide: {
    display: 'inline-block',
    minWidth: '100%',
  },
  assetReviewsAssetGroup: {
    width: '100%',
  },
  assetReviewsExpandedPanel: {
    background: EXPANDED_ROW_BACKGROUND,
    borderBottom: `thin solid ${EXPANDED_ROW_BORDER}`,
    boxShadow: `inset 3px 0 0 ${theme.palette.secondary.main}`,
    boxSizing: 'border-box',
    width: '100%',
  },
  assetReviewsActionsCell: {
    cursor: 'auto',
    textAlign: 'center',
  },
  tableScrollWrapperWithActions: {
    // Override Table's td z-index:-1 so row clicks are not lost under the sticky actions column.
    '& > div > table > tbody > tr:not([data-row-type="expanded"]) > td': {
      position: 'relative',
      zIndex: 1,
    },
    '& > div > table > thead > tr > th:nth-last-child(1), & > div > table > tbody > tr:not([data-row-type="expanded"]) > td:nth-last-child(1)':
      {
        boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.06)',
        minWidth: 56,
        padding: theme.spacing(1, 0.5),
        position: 'sticky',
        right: 0,
        verticalAlign: 'middle',
        zIndex: 2,
      },
    '& > div > table > thead > tr > th:nth-last-child(1)': {
      background: TABLE_HEADER_BACKGROUND,
      borderTopRightRadius: 8,
      boxShadow: '-4px 0 8px rgba(16, 24, 40, 0.06)',
      zIndex: 3,
    },
    '& > div > table > tbody > tr:not([data-row-type="expanded"]) > td:nth-last-child(1)': {
      background: theme.palette.common.white,
      zIndex: 2,
      '&::before': {
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent)',
        bottom: 0,
        content: '""',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        width: 8,
        zIndex: 0,
      },
      '& > span': {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
      },
    },
  },
  accordionSubTableWrapper: {
    boxSizing: 'border-box',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    '& > div': {
      borderRadius: 0,
      boxShadow: 'none',
      display: 'block',
      minWidth: '100%',
      overflow: 'visible',
      width: 'max-content',
    },
    '& > div > table': {
      minWidth: '100%',
      tableLayout: 'auto',
      width: '100%',
    },
    '& > div > table > thead th': {
      whiteSpace: 'nowrap',
    },
    '&$tableScrollWrapperWithActions > div > table > tbody > tr > td': {
      position: 'relative',
      zIndex: 1,
    },
    '&$tableScrollWrapperWithActions > div > table > thead > tr > th:nth-last-child(1), &$tableScrollWrapperWithActions > div > table > tbody > tr > td:nth-last-child(1)':
      {
        boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.06)',
        minWidth: 56,
        padding: theme.spacing(1, 0.5),
        position: 'sticky',
        right: 0,
        verticalAlign: 'middle',
        zIndex: 2,
      },
    '&$tableScrollWrapperWithActions > div > table > thead > tr > th:nth-last-child(1)': {
      background: TABLE_HEADER_BACKGROUND,
      borderTopRightRadius: 0,
      boxShadow: '-4px 0 8px rgba(16, 24, 40, 0.06)',
      zIndex: 3,
    },
    '&$tableScrollWrapperWithActions > div > table > tbody > tr > td:nth-last-child(1)': {
      background: theme.palette.common.white,
      zIndex: 2,
      '&::before': {
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent)',
        bottom: 0,
        content: '""',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        width: 8,
        zIndex: 0,
      },
      '& > span': {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
      },
    },
  },
  assetNameLink: {
    font: 'inherit',
  },
  assetNameLinkExpanded: {
    color: theme.palette.primary.main,
  },
  warningChipsCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    maxWidth: 280,
  },
  panelActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  statusDot: {
    borderRadius: '50%',
    flexShrink: 0,
    height: 8,
    width: 8,
  },
  statusDotWarning: {
    backgroundColor: theme.palette.warning.main,
  },
  statusDotInfo: {
    backgroundColor: theme.palette.info.main,
  },
  statusDotSuccess: {
    backgroundColor: theme.palette.success.main,
  },
  statusDotDanger: {
    backgroundColor: theme.palette.error.main,
  },
  statusCell: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  dpiaBadge: {
    borderRadius: 999,
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: theme.spacing(0.25, 1),
  },
  dpiaBadgeDanger: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  dpiaBadgeWarn: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  dpiaBadgeSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  dpiaBadgeInfo: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  dpiaPreviousHint: {
    display: 'block',
    fontSize: '0.75rem',
    marginTop: 4,
    opacity: 0.7,
  },
  rowActions: {
    display: 'flex',
    justifyContent: 'center',
  },
  assetFooter: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  bulkBlocked: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  emptyProcessingState: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  emptyActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  emptyState: {
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  ownerWorkspaceEmptyState: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  forbiddenState: {
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  errorBanner: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  noticeBanner: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'left',
  },
  noticeBannerWarning: {
    backgroundColor: '#fffaf0',
    borderColor: '#f0d7a0',
  },
  noticeBannerDanger: {
    backgroundColor: '#fff3f2',
    borderColor: '#efc1bf',
  },
  riskPanelHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  riskPanelTitle: {
    flex: '1 1 12rem',
    minWidth: 0,
  },
  permanentStatusLine: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  modalSummaryList: {
    color: theme.palette.text.secondary,
    margin: 0,
    paddingLeft: theme.spacing(2.5),
  },
  modalScopeSummary: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  closureBanner: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  closureComment: {
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
  },
  twoColumnLayout: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'minmax(280px, 1fr) minmax(0, 2fr)',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  sidePanel: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  mainPanel: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  fieldList: {
    '& > div + div': {
      marginTop: theme.spacing(1.5),
    },
  },
  fieldLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  preLineValue: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  },
  categoryTagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  categoryChip: {
    backgroundColor: '#EEF3FF',
    fontWeight: 500,
  },
  reviewBanner: {
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  reviewBannerRow: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  completenessList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1),
  },
  completenessItem: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1.25),
    lineHeight: 1.5,
  },
  completenessItemIcon: {
    flexShrink: 0,
    fontWeight: 700,
    width: theme.spacing(2),
  },
  completenessItemComplete: {
    color: theme.palette.text.primary,
  },
  completenessItemIncomplete: {
    color: theme.palette.error.main,
  },
  formActions: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
  },
  formActionsSectionFooter: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  pageActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  centeredPageActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  tooltipTrigger: {
    display: 'inline-flex',
  },
  adminDpiaReviewSupportZone: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(4),
    padding: theme.spacing(2.5),
    '& $apaSection': {
      marginTop: 0,
    },
    '& $apaSection + $apaSection': {
      marginTop: theme.spacing(3),
    },
  },
  adminDpiaReviewDecisionSection: {
    backgroundColor: '#f3f8fc',
    border: '1px solid #c9dff1',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(4),
    padding: theme.spacing(2.5),
  },
  apaSection: {
    '& + &': {
      marginTop: theme.spacing(3),
    },
  },
  apaSectionHeader: {
    marginBottom: theme.spacing(2),
  },
  apaSectionTitle: {
    marginBottom: theme.spacing(1),
    marginTop: 0,
  },
  apaSectionHelp: {
    color: theme.palette.text.secondary,
    margin: 0,
  },
  apaSectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '& $metricsRowAuto': {
      marginBottom: 0,
    },
    '& $noticeBanner': {
      marginBottom: 0,
    },
    '& $purposePanel': {
      marginBottom: 0,
    },
    '& $reviewBanner': {
      marginBottom: 0,
    },
    '& $reviewSummaryMetricsRow': {
      marginBottom: 0,
    },
  },
  apaSectionFields: {
    '& .MuiFormControl-root': {
      marginBottom: 0,
      marginTop: 0,
    },
  },
  apaSectionNote: {
    color: theme.palette.text.secondary,
    margin: 0,
  },
  reviewDecisionOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  reviewDecisionComment: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(0.5),
  },
  reviewDecisionSubmit: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  apaSectionBody: {
    marginTop: theme.spacing(2),
  },
  purposePanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
  purposeSummary: {
    cursor: 'pointer',
    fontWeight: 600,
  },
  purposePanelBody: {
    marginTop: theme.spacing(1.5),
  },
  purposeCentralBlock: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  purposeAssetBlock: {
    paddingTop: theme.spacing(1),
  },
  countryRowCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
  textButtonLink: {
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    font: 'inherit',
    marginTop: theme.spacing(1),
    padding: 0,
    textDecoration: 'underline',
  },
  revisionMeta: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  introPanel: {
    alignItems: 'flex-start',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  workspaceSummaryGrid: {
    alignItems: 'center',
    display: 'grid',
    flex: '1 1 auto',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    width: '100%',
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginBottom: theme.spacing(0.25),
    textTransform: 'uppercase',
  },
  summaryValue: {
    fontSize: '0.8125rem',
  },
  newPurposeBadge: {
    backgroundColor: theme.palette.warning.light,
    borderRadius: 999,
    color: theme.palette.warning.dark,
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
  },
}));

export default useStyles;
